import type { SitePrivateConfig, SitePublicConfig } from '@centric-os/types';
import { defineStore, type StateTree } from 'pinia';
import { useSiteConfigStore } from '../use-site-config-store';

/**
 * Interface for the state of the `useCashlessPayStore`.
 */
interface State extends StateTree {}

/**
 * Pinia store for managing cashless payment configurations.
 * This store provides actions to save both private and public cashless payment configurations for a site.
 *
 * @param {string} [storeId='cashlessPay'] - The unique ID of the store.
 * @returns {import('pinia').Store} The Pinia store instance for managing cashless payment configurations.
 */
export function useCashlessPayStore(storeId = 'cashlessPay') {
  const store = defineStore(storeId, {
    state: (): State => ({}),
    actions: {
      /**
       * Saves both private and public cashless payment configurations.
       * Depending on the `isSiteConfig` flag, it will either save configurations for a site or for another entity (e.g., a station).
       *
       * @param {string} id - The ID of the entity (site or station) for which the configurations are being saved.
       * @param {boolean} isSiteConfig - Flag indicating whether the configurations are for a site (true) or another entity like a station (false).
       * @param {Partial<SitePrivateConfig>} privatePayload - The private configuration data to be saved.
       * @param {Partial<SitePublicConfig>} publicPayload - The public configuration data to be saved.
       * @returns {Promise<void>} A promise that resolves once both private and public configurations are saved.
       */
      async saveCashlessPay(
        id: string,
        isSiteConfig: boolean,
        privatePayload: Partial<SitePrivateConfig>,
        publicPayload: Partial<SitePublicConfig>,
      ): Promise<void> {
        // Save private and public cashless pay configurations for site
        if (isSiteConfig) {
          // Set site configurations store
          const siteConfigStore = useSiteConfigStore();

          // Save both private and public site configurations concurrently
          await Promise.all([
            siteConfigStore.savePrivate(id, privatePayload),
            siteConfigStore.savePublic(id, publicPayload),
          ]);
        } else {
          // Save private and public cashless pay configurations for station (to be implemented)
        }
      },
    },
    pagination: false,
  });

  return store();
}
